// Existing human-authenticated GET readers only. Native <details> keeps focus and
// scroll in this row; no signal write rebuilds the surrounding cashbook register.
import { api } from '../shared/api.js';
import { t } from '../shared/i18n.js';
import { fmtMoney } from './bank_helpers.js';

const cents = value => Math.round(Number(value) * 100);
const positiveId = value => Number.isSafeInteger(Number(value)) && Number(value) > 0;
const element = (tag, text, cls) => {
  const node = document.createElement(tag);
  if(text !== undefined) node.textContent = text;
  if(cls) node.className = cls;
  return node;
};

export function settlementDetails(companyId, accountId, line, symbol, cache){
  const key = [accountId, line.id, line.journal_id, line.amount].join(':');
  const state = cache.get(key) || { open: false, settlement: null, documents: null, pending: null };
  cache.set(key, state);
  const details = element('details', undefined, 'cb-settlement');
  const summary = element('summary', undefined, 'alloc-btn-matched mono');
  const body = element('div', undefined, 'cb-settlement-body');
  body.setAttribute('aria-live', 'polite');
  details.append(summary, body);
  details.open = state.open;

  const incoming = Number(line.amount) > 0;
  // Review 2026-10-19: the label comes from the register's own counts (settlement_count = invoices
  // paid, settlement_contact_count = customers/suppliers), so nothing is read until the row is opened.
  // One contact paying several invoices is that contact, not "3 customer payments".
  const invoices = Number(line.settlement_count) || 0, contacts = Number(line.settlement_contact_count) || 0;
  const invoicesWord = n => n === 1 ? t('cashbook.invoice_one', '1 invoice') : n + ' ' + t('cashbook.invoices_word', 'invoices');
  const label = () => {
    if(contacts <= 1 && line.contact_name) return line.contact_name + ' · ' + invoicesWord(invoices);
    const who = incoming ? t('cashbook.customer_payments', 'customer payments') : t('cashbook.supplier_payments', 'supplier payments');
    return contacts + ' ' + who + (invoices !== contacts ? ' · ' + invoicesWord(invoices) : '');
  };
  summary.textContent = label();
  const unavailable = () => {
    // the label stays as it was; the problem shows inside the opened row only
    body.replaceChildren(element('p', t('cashbook.payment_details_retry_hint', 'The payment details could not be loaded.')));
    const retry = element('button', t('common.retry', 'Retry'), 'btn btn-sm btn-outline');
    retry.type = 'button';
    retry.addEventListener('click', e => { e.stopPropagation(); state.pending = null; state.settlement = null; state.documents = null; load(); });
    body.append(retry);
  };
  const validate = data => {
    const actual = data?.line, allocations = data?.allocations;
    if(data?.posted !== true || actual?.status !== 'posted' || actual?.match_type !== 'document'
      || Number(actual.id) !== Number(line.id) || Number(actual.company_id) !== Number(companyId)
      || Number(actual.bank_account_id) !== Number(accountId) || Number(actual.journal_id) !== Number(line.journal_id)
      || cents(actual.amount) !== cents(line.amount) || !Array.isArray(allocations) || !allocations.length
      || !Number.isFinite(Number(data.settled_total))) throw new Error('Invalid settlement');
    const seen = new Set();
    let total = 0;
    for(const allocation of allocations){
      if(!positiveId(allocation.document_id) || !positiveId(allocation.contact_id) || !positiveId(allocation.open_item_id)
        || seen.has(Number(allocation.open_item_id)) || !Number.isFinite(Number(allocation.amount)) || cents(allocation.amount) <= 0)
        throw new Error('Invalid allocation');
      seen.add(Number(allocation.open_item_id)); total += cents(allocation.amount);
    }
    if(total !== Math.abs(cents(line.amount)) || total !== cents(data.settled_total)) throw new Error('Incomplete settlement');
    return data;
  };
  async function documentLabels(){
    if(state.documents) return state.documents;
    if(state.documentPending) return state.documentPending;
    const allocations = state.settlement.allocations;
    const documents = new Array(allocations.length);
    let next = 0, failed = false;
    // Bound concurrent document reads. No document requests happen before expansion; the first bad
    // read stops the other workers instead of letting them fetch the rest.
    state.documentPending = Promise.all(Array.from({ length: Math.min(4, allocations.length) }, async () => {
      while(!failed && next < allocations.length){
        const i = next++, allocation = allocations[i];
        const result = await api.get(`/companies/${companyId}/documents/${allocation.document_id}`);
        const doc = result?.document;
        if(Number(doc?.id) !== Number(allocation.document_id) || Number(doc?.company_id) !== Number(companyId)
          || Number(doc?.contact_id) !== Number(allocation.contact_id) || !doc.document_no || !doc.contact_name){
          failed = true; throw new Error('Invalid document label');
        }
        documents[i] = doc;
      }
    })).then(() => { state.documents = documents; return documents; });
    try { return await state.documentPending; }
    finally { state.documentPending = null; }
  }
  function renderRows(documents){
    const table = element('table', undefined, 'cb-settlement-table');
    const caption = element('caption', t('cashbook.matched_payments', 'Matched payments'));
    const head = element('thead'), header = element('tr');
    for(const text of [incoming ? t('common.customer', 'Customer') : t('common.supplier', 'Supplier'),
      t('common.invoice', 'Invoice'), t('cashbook.allocated_amount', 'Allocated amount')]){
      const cell = element('th', text); cell.scope = 'col'; header.append(cell);
    }
    head.append(header);
    const rows = element('tbody');
    state.settlement.allocations.forEach((allocation, i) => {
      const row = element('tr');
      row.append(element('td', documents[i].contact_name), element('td', documents[i].document_no, 'mono'),
        element('td', fmtMoney(allocation.amount, symbol), 'mono'));
      rows.append(row);
    });
    const foot = element('tfoot'), total = element('tr');
    const title = element('th', t('cashbook.batch_total', 'Batch total')); title.scope = 'row'; title.colSpan = 2;
    total.append(title, element('td', fmtMoney(state.settlement.settled_total, symbol), 'mono'));
    foot.append(total); table.append(caption, head, rows, foot); body.replaceChildren(table);
  }
  async function load(){
    try {
      if(!state.settlement){
        state.pending ||= api.get(`/companies/${companyId}/bank-lines/${line.id}/settlement`).then(validate);
        state.settlement = await state.pending;
      }
      if(details.open){
        body.replaceChildren(element('p', t('cashbook.payment_details_loading', 'Loading payment details…')));
        renderRows(await documentLabels());
      }
    } catch { state.pending = null; unavailable(); }
  }
  details.addEventListener('click', e => e.stopPropagation());
  details.addEventListener('toggle', () => { state.open = details.open; if(details.open) load(); });
  if(details.open) load();          // re-rendered while open: refill it; otherwise nothing is read
  return details;
}
